class CartDrawerProductsRecommendationV2 extends HTMLElement {
  connectedCallback() {
    this.listEl = this.querySelector('[data-list]');
    this.jsonEl = this.querySelector('[data-cart-drawer-recs-v2-json]');
    if (!this.listEl || !this.jsonEl) return;

    let payload;
    try {
      payload = JSON.parse(this.jsonEl.textContent.trim());
    } catch (e) {
      console.warn('[cart-recs-v2] JSON parse failed', e);
      return;
    }

    console.log('[cart-recs-v2] payload', payload);
    const picks = this.computeRecommendations(payload);
    console.log('[cart-recs-v2] picks', picks);
    if (!picks.length) {
      this.setAttribute('hidden', '');
      return;
    }

    this.renderRows(picks);
    this.removeAttribute('hidden');
    this.bindAddButtons();
  }

  /**
   * Pull the zones value out of a payload row, trying every shape we've seen
   * Liquid templates emit: top-level key, snake_case, or nested under metafields.
   */
  pickZonesField(row) {
    if (!row) return null;
    if (row.growingZones != null) return row.growingZones;
    if (row.growing_zones != null) return row.growing_zones;
    const mf = row.metafields;
    if (mf) {
      if (mf.growingZones != null) return mf.growingZones;
      if (mf.growing_zones != null) return mf.growing_zones;
      const c = mf.custom;
      if (c) {
        if (c.growingZones != null) return c.growingZones;
        if (c.growing_zones != null) return c.growing_zones;
      }
    }
    return null;
  }

  /**
   * Normalize a growing_zones metafield value into an array of numbers.
   * Accepts: array of numbers/strings, JSON string, or comma-separated string.
   */
  parseZones(raw) {
    if (raw == null) return [];
    let value = raw;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      // Try JSON first (Shopify list metafields are commonly JSON-encoded).
      if (trimmed.startsWith('[')) {
        try {
          value = JSON.parse(trimmed);
        } catch (e) {
          value = trimmed.split(',');
        }
      } else {
        value = trimmed.split(',');
      }
    }

    if (!Array.isArray(value)) value = [value];

    const out = [];
    for (const v of value) {
      if (v == null) continue;
      const n = Number(String(v).trim());
      if (Number.isFinite(n)) out.push(n);
    }
    return out;
  }

  /**
   * Build a frequency map of growing zones across cart items.
   * Each cart line contributes once per zone it lists.
   */
  buildCartZoneFrequency(cartItems) {
    const freq = new Map();
    for (const line of cartItems) {
      const zones = this.parseZones(this.pickZonesField(line));
      const seen = new Set();
      for (const z of zones) {
        if (seen.has(z)) continue;
        seen.add(z);
        freq.set(z, (freq.get(z) || 0) + 1);
      }
    }
    return freq;
  }

  /**
   * Score = sum over candidate zones of cart frequency for that zone.
   * Also returns the count of distinct zones shared with the cart.
   */
  zoneOverlap(candidateZones, cartZoneFreq) {
    let weighted = 0;
    let shared = 0;
    for (const z of candidateZones) {
      if (cartZoneFreq.has(z)) {
        weighted += cartZoneFreq.get(z);
        shared += 1;
      }
    }
    return { weighted, shared };
  }

  mergeCandidates(rawCandidates) {
    const byId = new Map();
    for (const row of rawCandidates) {
      if (!row || row.id == null) continue;
      const id = row.id;
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, { ...row });
      }
    }
    return [...byId.values()];
  }

  computeRecommendations({ cartItems = [], candidates = [] }) {
    const inCartVariantIds = new Set(cartItems.map((l) => l.variantId));
    const inCartProductIds = new Set(cartItems.map((l) => l.productId));

    const cartZoneFreq = this.buildCartZoneFrequency(cartItems);
    console.log('[cart-recs-v2] cart zone frequency', cartZoneFreq);

    if (cartZoneFreq.size === 0) {
      console.warn(
        '[cart-recs-v2] No growing_zones found on any cart item. ' +
          'Make sure the Liquid template includes growing_zones on each cartItems entry.'
      );
    }

    const merged = this.mergeCandidates(candidates);
    const eligible = [];
    const scored = [];

    for (const p of merged) {
      // Skip gift cards
      if (p.giftCard) continue;

      // Skip products that are not available or have no available variant
      if (!p.available || !p.variantAvailable) continue;

      // Skip products that are already in the cart
      if (inCartVariantIds.has(p.variantId) || inCartProductIds.has(p.id)) continue;

      // Skip products that require a selling plan
      if (p.requiresSellingPlan) continue;

      // Skip products that have multiple variants
      const simple = p.hasOnlyDefaultVariant === true || Number(p.variantsCount) === 1;
      if (!simple) continue;

      eligible.push(p);

      const candZones = this.parseZones(this.pickZonesField(p));
      const { weighted, shared } = this.zoneOverlap(candZones, cartZoneFreq);

      // Score: heavier weight on number of distinct shared zones, plus a
      // frequency-weighted bonus when many cart items care about the same zone.
      const score = shared * 100 + weighted * 10;

      scored.push({ ...p, _zoneScore: score, _sharedZones: shared, _candZones: candZones });
    }

    console.log('[cart-recs-v2] scored', scored);

    // Higher score first, then cheaper first as a tiebreaker.
    // scored.sort((a, b) => b._zoneScore - a._zoneScore || a.price - b.price);
    scored.sort((a, b) => b._zoneScore - a._zoneScore);

    // Prefer products that share at least one zone with the cart. If none do
    // (e.g. metafield missing on candidates) fall back to the eligible list
    // ordered by price so the section still renders something useful.
    const withOverlap = this.gapFirst(scored.filter((p) => p._sharedZones > 0));
    if (withOverlap.length) return withOverlap.slice(0, 2);

    console.warn(
      '[cart-recs-v2] No candidates shared any zones with cart. Falling back to price-sorted eligible products.'
    );
    return eligible.sort((a, b) => a.price - b.price).slice(0, 2);
  }

  // A/B variant: products that cover the free shipping gap go first, zone order kept otherwise.
  gapFirst(list) {
    if (this.dataset.abGapFill !== 'true') return list;

    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const goalCents = Number(this.dataset.abGoal) * rate * 100;
    const remaining = goalCents - Number(this.dataset.abCartTotal);
    if (!(goalCents > 0) || !(remaining > 0)) return list;

    const closers = list.filter((p) => p.price >= remaining).sort((a, b) => b._zoneScore - a._zoneScore || a.price - b.price);
    return [...closers, ...list.filter((p) => p.price < remaining)];
  }

  renderRows(products) {
    const moneyFormat = window.FoxTheme?.settings?.moneyFormat || '${{amount}}';
    const fmt = (cents) =>
      window.FoxTheme?.Currency?.formatMoney ? FoxTheme.Currency.formatMoney(cents, moneyFormat) : '';

    const root = (this.dataset.rootUrl || '').replace(/\/$/, '');
    this.listEl.innerHTML = products
      .map((p) => {
        const priceHtml = fmt(p.price);
        const productHref = `${root}/products/${p.handle}`;
        const img = p.featuredImage
          ? `<img src="${this.escapeAttr(p.featuredImage)}" alt="" width="64" height="64" loading="lazy">`
          : `<span class="cart-drawer-recommendations-v2__placeholder" aria-hidden="true"></span>`;
        const label = this.escapeHtml(this.dataset.addLabel || 'Add');
        return `
          <li class="cart-drawer-recommendations-v2__row">
            <a href="${this.escapeAttr(productHref)}" class="cart-drawer-recommendations-v2__media blocks-radius-sm media-wrapper" tabindex="-1">${img}</a>
            <div class="cart-drawer-recommendations-v2__meta">
              <a href="${this.escapeAttr(productHref)}" class="cart-drawer-recommendations-v2__title text-pcard-title reversed-link">${this.escapeHtml(p.title)}</a>
              <div class="cart-drawer-recommendations-v2__price text-sm font-body-bolder">${priceHtml}</div>
            </div>
            <button type="button" class="btn btn--secondary cart-drawer-recommendations-v2__add" data-variant-id="${p.variantId}">
              <span>${label}</span>
              <div class="loading__spinner hidden">
                <svg aria-hidden="true" focusable="false" class="spinner icon icon--small" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </button>
          </li>
        `;
      })
      .join('');
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replace(/'/g, '&#39;');
  }

  bindAddButtons() {
    this.querySelectorAll('[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-variant-id');
        if (id) this.addVariant(id, btn);
      });
    });
  }

  /**
   * Format today's date as YYYY-MM-DD for the _cart_addon property.
   */
  todayStamp() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  async addVariant(variantId, button) {
    button.classList.add('btn--loading');
    button.setAttribute('disabled', '');

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _cart_addon: true,
          },
        },
      ],
      sections: sectionsToBundle,
      section_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body,
      });
      const parsedState = await response.json();
      if (parsedState.status) {
        alert(parsedState.description || parsedState.message || 'Unable to add to cart');
        return;
      }
      const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
      cartJson['sections'] = parsedState['sections'];
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    } catch (err) {
      console.error(err);
    } finally {
      button.classList.remove('btn--loading');
      button.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('cart-drawer-products-recommendation-v2')) {
  customElements.define('cart-drawer-products-recommendation-v2', CartDrawerProductsRecommendationV2);
}